import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/react";
import { toast } from "sonner";
import { User, Store } from "lucide-react";
import { useAuthedApi, errMsg, formatINR } from "../lib/api";
import { BRANDS, FUELS, TRANSMISSIONS, BODY_TYPES } from "../data/india";
import StateCitySelect from "../components/StateCitySelect";
import ImageUploader from "../components/ImageUploader";

const EMPTY = { seller_type: "private", seller_name: "", seller_phone: "", brand: "", model: "", variant: "", year: "", price: "", km_driven: "", fuel: "Petrol", transmission: "Manual", owners: 1, body_type: "", color: "", state: "", city: "", description: "", images: [] };

const Field = ({ label, children }) => <div><label className="lux-label">{label}</label>{children}</div>;

export default function Sell() {
  const api = useAuthedApi();
  const { user } = useUser();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [me, setMe] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("get", "/me").then((m) => {
      setMe(m);
      if (!editId) setF((p) => ({ ...p, seller_name: p.seller_name || m.name || user?.fullName || "", seller_phone: p.seller_phone || m.phone || "" }));
    });
    if (editId) api("get", "/listings/mine").then((mine) => {
      const found = mine.find((x) => x.id === editId);
      if (!found) return toast.error("Listing not found");
      const { id, owner_id, dealer_id, status, is_demo, created_at, ...rest } = found;
      setF({ ...EMPTY, ...rest });
    });
  }, [api, editId, user]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const chooseType = (t) => {
    if (t === "dealer" && me?.dealer) setF({ ...f, seller_type: t, seller_name: me.dealer.name, seller_phone: me.dealer.phone, state: f.state || me.dealer.state, city: f.city || me.dealer.city });
    else setF({ ...f, seller_type: t });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.images.length) return toast.error("Please add at least one photo");
    if (!f.state || !f.city) return toast.error("Please select state and city");
    setBusy(true);
    try {
      const body = { ...f, year: Number(f.year), price: Number(f.price), km_driven: Number(f.km_driven), owners: Number(f.owners), offer_min: Number(f.offer_min) || null, offer_max: Number(f.offer_max) || null };
      const res = editId ? await api("put", `/listings/${editId}`, body) : await api("post", "/listings", body);
      toast.success(editId ? "Listing updated" : "Your car is now live!");
      navigate(`/cars/${res.id}`);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32" data-testid="sell-page">
      <p className="eyebrow mb-3">{editId ? "Edit listing" : "List your car — free"}</p>
      <h1 className="font-display text-5xl sm:text-6xl mb-10">{editId ? "Update your car" : "Sell your car"}</h1>
      <form onSubmit={submit} className="space-y-8">
        <div className="lux-card p-6 sm:p-8 space-y-5">
          <p className="eyebrow">01 · I am selling as</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["private", User, "Private Owner", "Selling my own car"], ["dealer", Store, "Dealer Store", me?.dealer ? `As ${me.dealer.name}` : "Requires a dealer store"]].map(([v, Icon, t, s]) => (
              <button type="button" key={v} onClick={() => chooseType(v)} data-testid={`sell-type-${v}`}
                className={`text-left p-5 rounded-2xl border transition-colors ${f.seller_type === v ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-[#D4AF37]/40"}`}>
                <Icon className="w-5 h-5 text-[#D4AF37] mb-2" /><p className="font-semibold">{t}</p><p className="text-xs text-gray-400">{s}</p>
              </button>
            ))}
          </div>
          {f.seller_type === "dealer" && me && !me.dealer && (
            <p className="text-sm text-amber-300" data-testid="no-store-warning">You don't have a dealer store yet. <Link to="/dashboard?tab=store" className="underline">Create your store</Link> first.</p>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Seller name"><input className="lux-input" required value={f.seller_name} onChange={set("seller_name")} data-testid="sell-seller-name" /></Field>
            <Field label="Mobile number (shown to signed-in buyers)"><input className="lux-input" required pattern="[0-9+ ]{10,15}" value={f.seller_phone} onChange={set("seller_phone")} data-testid="sell-seller-phone" /></Field>
          </div>
        </div>

        <div className="lux-card p-6 sm:p-8 space-y-5">
          <p className="eyebrow">02 · Car details</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Brand"><select className="lux-select" required value={f.brand} onChange={set("brand")} data-testid="sell-brand"><option value="">Select</option>{BRANDS.map((b) => <option key={b}>{b}</option>)}</select></Field>
            <Field label="Model"><input className="lux-input" required value={f.model} onChange={set("model")} placeholder="e.g. Creta" data-testid="sell-model" /></Field>
            <Field label="Variant"><input className="lux-input" value={f.variant} onChange={set("variant")} placeholder="e.g. SX(O)" data-testid="sell-variant" /></Field>
            <Field label="Year"><input className="lux-input" type="number" required min="1980" max={new Date().getFullYear()} value={f.year} onChange={set("year")} data-testid="sell-year" /></Field>
            <Field label="KM driven"><input className="lux-input" type="number" required min="0" value={f.km_driven} onChange={set("km_driven")} data-testid="sell-km" /></Field>
            <Field label="Owners"><select className="lux-select" value={f.owners} onChange={set("owners")} data-testid="sell-owners">{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
            <Field label="Fuel"><select className="lux-select" value={f.fuel} onChange={set("fuel")} data-testid="sell-fuel">{FUELS.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Transmission"><select className="lux-select" value={f.transmission} onChange={set("transmission")} data-testid="sell-transmission">{TRANSMISSIONS.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Body type"><select className="lux-select" value={f.body_type} onChange={set("body_type")} data-testid="sell-body"><option value="">Select</option>{BODY_TYPES.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Colour"><input className="lux-input" value={f.color} onChange={set("color")} data-testid="sell-color" /></Field>
            <Field label="Asking price (₹)"><input className="lux-input" type="number" required min="1" value={f.price} onChange={set("price")} data-testid="sell-price" />{f.price > 0 && <p className="text-xs text-[#E5C158] mt-1">{formatINR(Number(f.price))}</p>}</Field>
            <Field label="Min offer (₹, optional)"><input className="lux-input" type="number" min="1" value={f.offer_min || ""} onChange={set("offer_min")} placeholder={f.price ? String(Math.round(f.price * 0.7)) : "70% of price"} data-testid="sell-offer-min" /><p className="text-[10px] text-gray-500 mt-1">Default 70% of price</p></Field>
            <Field label="Max offer (₹, optional)"><input className="lux-input" type="number" min="1" value={f.offer_max || ""} onChange={set("offer_max")} placeholder={f.price ? String(f.price) : "Asking price"} data-testid="sell-offer-max" /><p className="text-[10px] text-gray-500 mt-1">Default = asking price</p></Field>
          </div>
          <Field label="Description"><textarea className="lux-textarea" rows={4} value={f.description} onChange={set("description")} placeholder="Service history, insurance, accessories, condition..." data-testid="sell-description" /></Field>
        </div>

        <div className="lux-card p-6 sm:p-8 space-y-5">
          <p className="eyebrow">03 · Location</p>
          <div className="grid sm:grid-cols-2 gap-4"><StateCitySelect state={f.state} city={f.city} prefix="sell-" onChange={(v) => setF({ ...f, ...v })} /></div>
        </div>

        <div className="lux-card p-6 sm:p-8 space-y-5">
          <p className="eyebrow">04 · Photos (first photo is the cover)</p>
          <ImageUploader value={f.images} onChange={(fn) => setF((p) => ({ ...p, images: fn(p.images) }))} testid="sell-photos" />
        </div>

        <button className="btn-gold w-full sm:w-auto !px-12 !py-4 text-base" disabled={busy} data-testid="sell-submit-button">{busy ? "Publishing..." : editId ? "Save changes" : "Publish listing"}</button>
      </form>
    </div>
  );
}
